import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails,
  Button, Alert, Divider, IconButton, Tooltip, Snackbar, LinearProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Menu, MenuItem, ListItemIcon, ListItemText, Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShareIcon from '@mui/icons-material/Share';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import { QRCodeSVG } from 'qrcode.react';
import { fetchScan, setPolling, clearCurrentScan, setScanQuickWins, type QuickWinSelectionState } from '../store/scanSlice.js';
import { api } from '../api/client.js';
import type { RootState, AppDispatch } from '../store/index.js';
import LoadingSpinner from '../components/LoadingSpinner.js';
import ScoreGauge from '../components/ScoreGauge.js';
import { colors } from '../styles/theme.js';
import { buildShareUrl } from '../shareUrl.js';
import { Rating, SCAN_LABEL_KEYS, TERMINAL_SCAN_LABEL_KEYS, UserRole } from '../../shared/types.js';
import ScanStatusChip from '../components/ScanStatusChip.js';
import type { IEvaluationResult, ICategoryEval, ICheckEval, IQuickWin, IScanRecommendation } from '../../shared/types.js';
import {
  normalizeScoreCardHtmlTemplate,
  SCORE_CARD_HTML_TEMPLATE_LABELS,
} from '../../shared/scoreCardHtmlTemplates.js';

/* ── Script Display Names ────────────────────────────────── */

const SCRIPT_LABELS: Record<string, { label: string; category: string }> = {
  googletagmanager: { label: 'Google Tag Manager', category: 'Tag Management' },
  tagmanager:       { label: 'Tag Manager (generic)', category: 'Tag Management' },
  google_analytics: { label: 'Google Analytics (GA4)', category: 'Analytics' },
  matomo:           { label: 'Matomo', category: 'Analytics' },
  plausible:        { label: 'Plausible', category: 'Analytics' },
  bing:             { label: 'Microsoft Ads (Bing)', category: 'Ads' },
  tiktok:           { label: 'TikTok Pixel', category: 'Ads' },
  doubleclick:      { label: 'Google Ads / DoubleClick', category: 'Remarketing' },
  facebook_pixel:   { label: 'Meta Pixel', category: 'Remarketing' },
  linkedin:         { label: 'LinkedIn Insight', category: 'Ads' },
  pinterest:        { label: 'Pinterest Tag', category: 'Ads' },
  hotjar:           { label: 'Hotjar', category: 'Heatmap' },
  mouseflow:        { label: 'Mouseflow', category: 'Heatmap' },
  crazyegg:         { label: 'Crazy Egg', category: 'Heatmap' },
  criteo:           { label: 'Criteo', category: 'Remarketing' },
};

const CATEGORY_COLORS: Record<string, string> = {
  'Tag Management': '#6366f1',
  Analytics:        '#0ea5e9',
  Ads:              '#f97316',
  Remarketing:      '#ec4899',
  Heatmap:          '#8b5cf6',
  Sonstige:         '#94a3b8',
};

function getScriptInfo(name: string) {
  return SCRIPT_LABELS[name] ?? { label: name, category: 'Sonstige' };
}

/* ── Screening-Vergleichstabelle ─────────────────────────── */

interface ScreeningData {
  initial: string[];
  accepted: string[];
  denied: string[];
}

/** Coerce screening API entries to script id strings (n8n may send objects, not only strings). */
function normalizeScreeningScriptList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      const t = item.trim();
      if (t) out.push(t);
      continue;
    }
    if (item != null && typeof item === 'object' && !Array.isArray(item)) {
      const o = item as Record<string, unknown>;
      const candidate = o.tool ?? o.name ?? o.script ?? o.id;
      if (typeof candidate === 'string' && candidate.trim()) {
        out.push(candidate.trim());
        continue;
      }
      if (typeof candidate === 'number') {
        out.push(String(candidate));
      }
      continue;
    }
    if (typeof item === 'number' || typeof item === 'boolean') {
      out.push(String(item));
    }
  }
  return out;
}

function extractScreeningData(raw: Record<string, unknown> | null): ScreeningData | null {
  if (!raw) return null;
  const data = raw as Record<string, unknown>;

  const ca = data.consent_accepted as Record<string, unknown> | undefined;
  const cd = data.consent_denied as Record<string, unknown> | undefined;
  const caScreen = ca?.screening as Record<string, unknown> | undefined;
  const cdScreen = cd?.screening as Record<string, unknown> | undefined;

  const initial = normalizeScreeningScriptList(
    caScreen?.screening_output_initial ?? cdScreen?.screening_output_initial ?? [],
  );
  const accepted = normalizeScreeningScriptList(caScreen?.screening_output_consent_accepted ?? []);
  const denied = normalizeScreeningScriptList(cdScreen?.screening_output_consent_denied ?? []);

  if (initial.length === 0 && accepted.length === 0 && denied.length === 0) return null;
  return { initial, accepted, denied };
}

function PresenceIcon({ active }: { active: boolean }) {
  if (active) return <DoneIcon sx={{ color: colors.scoreGreen, fontSize: 20 }} />;
  return <BlockIcon sx={{ color: '#d1d5db', fontSize: 20 }} />;
}

function ScreeningTable({ screening }: { screening: ScreeningData }) {
  const allScripts = useMemo(() => {
    const set = new Set([...screening.initial, ...screening.accepted, ...screening.denied]);
    return Array.from(set).sort((a, b) => {
      const catA = getScriptInfo(a).category;
      const catB = getScriptInfo(b).category;
      if (catA !== catB) return String(catA).localeCompare(String(catB));
      return String(a).localeCompare(String(b));
    });
  }, [screening]);

  const initialSet = useMemo(() => new Set(screening.initial), [screening.initial]);
  const acceptedSet = useMemo(() => new Set(screening.accepted), [screening.accepted]);
  const deniedSet = useMemo(() => new Set(screening.denied), [screening.denied]);

  let lastCategory = '';

  return (
    <Paper elevation={0} sx={{ mb: 3, border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
      <Box sx={{ p: 3, pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Tracking-Script Übersicht
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Vergleich der erkannten Scripts in den drei Consent-Zuständen
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: colors.black }}>
              <TableCell sx={{ color: colors.white, fontWeight: 600, width: '40%' }}>Script</TableCell>
              <TableCell align="center" sx={{ color: colors.white, fontWeight: 600 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span>Pre-Consent</span>
                  <Typography variant="caption" sx={{ color: colors.silver, fontSize: '0.65rem' }}>Initial</Typography>
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ color: colors.lime, fontWeight: 600 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span>Opt-In</span>
                  <Typography variant="caption" sx={{ color: colors.silver, fontSize: '0.65rem' }}>Accepted</Typography>
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ color: colors.scoreRed, fontWeight: 600 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span>Opt-Out</span>
                  <Typography variant="caption" sx={{ color: colors.silver, fontSize: '0.65rem' }}>Denied</Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allScripts.map((script) => {
              const info = getScriptInfo(script);
              const showCatHeader = info.category !== lastCategory;
              lastCategory = info.category;
              const catColor = CATEGORY_COLORS[info.category] ?? CATEGORY_COLORS.Sonstige;

              return (
                <TableRow
                  key={script}
                  sx={{
                    '&:hover': { bgcolor: '#f8fafc' },
                    ...(showCatHeader ? { borderTop: `2px solid ${catColor}` } : {}),
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {showCatHeader && (
                        <Chip
                          label={info.category}
                          size="small"
                          sx={{
                            bgcolor: catColor,
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: 20,
                          }}
                        />
                      )}
                      {!showCatHeader && <Box sx={{ width: 8 }} />}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {info.label}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <PresenceIcon active={initialSet.has(script)} />
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: acceptedSet.has(script) ? '#f0fdf4' : undefined }}>
                    <PresenceIcon active={acceptedSet.has(script)} />
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: deniedSet.has(script) ? '#fef2f2' : undefined }}>
                    <PresenceIcon active={deniedSet.has(script)} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 2, bgcolor: colors.lightGrey, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>{screening.initial.length}</strong> Scripts pre-consent
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{screening.accepted.length}</strong> Scripts nach Accept
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{screening.denied.length}</strong> Scripts nach Deny
        </Typography>
      </Box>
    </Paper>
  );
}

/* ── Rohdaten-Explorer (3 Accordions) ────────────────────── */

function KvRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600, width: '30%', verticalAlign: 'top', fontSize: '0.8rem', py: 1 }}>{label}</TableCell>
      <TableCell sx={{ fontSize: '0.8rem', py: 1, wordBreak: 'break-all' }}>{value}</TableCell>
    </TableRow>
  );
}

function SimpleTable({ children }: { children: React.ReactNode }) {
  return (
    <TableContainer>
      <Table size="small" sx={{ '& td, & th': { borderColor: '#e5e7eb' } }}>
        <TableBody>{children}</TableBody>
      </Table>
    </TableContainer>
  );
}

function DataSectionHeader({ title }: { title: string }) {
  return (
    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, px: 1, fontWeight: 600, color: colors.black60, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
      {title}
    </Typography>
  );
}

function ScriptListTable({ scripts, label }: { scripts: string[]; label: string }) {
  if (scripts.length === 0) return (
    <Box sx={{ px: 1, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Keine Scripts erkannt</Typography>
    </Box>
  );
  return (
    <>
      <DataSectionHeader title={label} />
      <SimpleTable>
        {scripts.map((s, i) => {
          const info = getScriptInfo(s);
          return (
            <KvRow key={i} label={info.label} value={<Chip label={info.category} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />} />
          );
        })}
      </SimpleTable>
    </>
  );
}

function DataLayerTable({ entries, label }: { entries: Record<string, unknown>[]; label: string }) {
  if (!entries || entries.length === 0) return null;
  return (
    <>
      <DataSectionHeader title={label} />
      <TableContainer>
        <Table size="small" sx={{ '& td, & th': { borderColor: '#e5e7eb' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', width: 40 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Typ</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, i) => {
              let typ = '—';
              let details = '';

              if (entry['0'] === 'consent' && entry['1'] === 'default') {
                typ = 'consent default';
                const obj = entry['2'] as Record<string, unknown> | undefined;
                details = obj ? Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join(', ') : '';
              } else if (entry['0'] === 'consent' && entry['1'] === 'update') {
                typ = 'consent update';
                const obj = entry['2'] as Record<string, unknown> | undefined;
                details = obj ? Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join(', ') : '';
              } else if (entry['0'] === 'set') {
                typ = 'set';
                details = `${entry['1']} = ${JSON.stringify(entry['2'])}`;
              } else if (entry['0'] === 'config') {
                typ = 'config';
                details = String(entry['1'] ?? '');
              } else if (entry['0'] === 'js') {
                typ = 'js';
                details = String(entry['1'] ?? '');
              } else if (typeof entry.event === 'string') {
                typ = 'event';
                details = entry.event;
              } else {
                typ = 'push';
                details = Object.keys(entry).join(', ');
              }

              return (
                <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                  <TableCell sx={{ fontSize: '0.75rem', color: colors.black60 }}>{i + 1}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    <Chip label={typ} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all' }}>{details}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function CmpButtonsTable({ buttons, label }: { buttons: Array<{ step: string; selector: string; tag: string; id: string; classes: string; text: string }>; label: string }) {
  if (!buttons || buttons.length === 0) return null;
  return (
    <>
      <DataSectionHeader title={label} />
      <TableContainer>
        <Table size="small" sx={{ '& td, & th': { borderColor: '#e5e7eb' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Step</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Text</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Selector</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Tag</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buttons.map((b, i) => (
              <TableRow key={i}>
                <TableCell sx={{ fontSize: '0.75rem' }}>{b.step}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{b.text}</TableCell>
                <TableCell sx={{ fontSize: '0.7rem', fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all' }}>{b.selector}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{b.tag}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function RuntimeInjectedTable({ items }: { items: Array<{ tool: string; src: string | null; snippet?: string | null; reason: string }> }) {
  if (!items || items.length === 0) return null;
  return (
    <>
      <DataSectionHeader title="Runtime Injected Tracking" />
      <TableContainer>
        <Table size="small" sx={{ '& td, & th': { borderColor: '#e5e7eb' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Tool</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Source / Snippet</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, i) => (
              <TableRow key={i}>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{getScriptInfo(item.tool).label}</TableCell>
                <TableCell sx={{ fontSize: '0.65rem', fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-all', maxWidth: 400 }}>
                  {item.src ? item.src.split('?')[0] : (item.snippet ? `${item.snippet.slice(0, 80)}…` : '—')}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem' }}>{item.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

interface RawBranch {
  hostname?: string;
  screening?: Record<string, unknown>;
  dataLayer_initial?: Record<string, unknown>[];
  dataLayer_consent_accepted?: { consentMode?: string; dataLayer?: Record<string, unknown>[] };
  dataLayer_consent_denied?: { consentMode?: string; dataLayer?: Record<string, unknown>[] };
  detectHardcoded?: boolean;
  detectHardcodedData?: unknown[];
  runtimeInjectedTracking?: Array<{ tool: string; src: string | null; snippet?: string | null; reason: string }>;
  detectHardcodedMeta?: Record<string, unknown>;
}

function RawDataExplorer({ raw: rawInput }: { raw: Record<string, unknown> }) {
  // Unwrap common n8n wrapper patterns so consent_accepted/consent_denied are accessible
  let raw = rawInput;
  if (!raw.consent_accepted && !raw.consent_denied) {
    // Try unwrapping from array wrapper
    if (Array.isArray(raw.items) && raw.items.length === 1 && typeof raw.items[0] === 'object' && raw.items[0] !== null) {
      raw = raw.items[0] as Record<string, unknown>;
    }
    // Try first key if it contains the expected structure
    const keys = Object.keys(raw);
    if (keys.length === 1 && typeof raw[keys[0]] === 'object' && raw[keys[0]] !== null) {
      const inner = raw[keys[0]] as Record<string, unknown>;
      if (inner.consent_accepted || inner.consent_denied) {
        raw = inner;
      }
    }
  }

  const ca = raw.consent_accepted as RawBranch | undefined;
  const cd = raw.consent_denied as RawBranch | undefined;

  const initialScripts = normalizeScreeningScriptList(
    ca?.screening?.screening_output_initial ?? cd?.screening?.screening_output_initial ?? [],
  );
  const initialDL = (ca?.dataLayer_initial ?? cd?.dataLayer_initial ?? []) as Record<string, unknown>[];

  const acceptScripts = normalizeScreeningScriptList(ca?.screening?.screening_output_consent_accepted ?? []);
  const acceptDL = ca?.dataLayer_consent_accepted?.dataLayer as Record<string, unknown>[] | undefined;
  const acceptCmpClicked = ca?.screening?.cmp_button_clicked as boolean | undefined;
  const acceptCmpMethod = ca?.screening?.cmp_click_method as string | undefined;
  const acceptCmpDetail = ca?.screening?.cmp_click_detail as string | undefined;
  const acceptCmpButtons = (ca?.screening?.cmp_clicked_buttons ?? []) as Array<{ step: string; selector: string; tag: string; id: string; classes: string; text: string }>;
  const acceptConsentMode = ca?.dataLayer_consent_accepted?.consentMode as string | undefined;

  const denyScripts = normalizeScreeningScriptList(cd?.screening?.screening_output_consent_denied ?? []);
  const denyDL = cd?.dataLayer_consent_denied?.dataLayer as Record<string, unknown>[] | undefined;
  const denyCmpClicked = cd?.screening?.cmp_deny_clicked as boolean | undefined;
  const denyCmpMethod = cd?.screening?.cmp_deny_method as string | undefined;
  const denyCmpDetail = cd?.screening?.cmp_deny_detail as string | undefined;
  const denyCmpButtons = (cd?.screening?.cmp_deny_clicked_buttons ?? []) as Array<{ step: string; selector: string; tag: string; id: string; classes: string; text: string }>;
  const denyConsentMode = cd?.dataLayer_consent_denied?.consentMode as string | undefined;

  const accentBar = (color: string) => ({ borderLeft: `4px solid ${color}`, pl: 2 });

  return (
    <Paper elevation={0} sx={{ mb: 3, border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
      <Box sx={{ p: 3, pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Rohdaten-Explorer</Typography>
        <Typography variant="body2" color="text.secondary">
          Alle Daten aus dem Playwright-Scan — ohne Bewertung, rein tabellarisch
        </Typography>
      </Box>

      {/* Initial (Pre-Consent) */}
      <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' }, borderTop: `1px solid ${colors.silver}` }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip label="Initial" sx={{ bgcolor: colors.black, color: colors.white, fontWeight: 600, fontSize: '0.8rem' }} />
            <Typography variant="body2" color="text.secondary">Pre-Consent — Zustand vor jeder Interaktion</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 2, ...accentBar(colors.black60) }}>
            <ScriptListTable scripts={initialScripts} label="Screening Output (Initial)" />
            <DataLayerTable entries={initialDL} label="DataLayer (Initial)" />
            {initialScripts.length === 0 && initialDL.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1 }}>Keine Pre-Consent-Daten vorhanden</Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Consent Accepted */}
      <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' }, borderTop: `1px solid ${colors.silver}` }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f0fdf4' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip label="Consent Accepted" sx={{ bgcolor: colors.scoreGreen, color: colors.white, fontWeight: 600, fontSize: '0.8rem' }} />
            <Typography variant="body2" color="text.secondary">Zustand nach Klick auf „Akzeptieren"</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 2, ...accentBar(colors.scoreGreen) }}>
            {(acceptCmpClicked != null || acceptCmpMethod) && (
              <>
                <DataSectionHeader title="CMP Interaktion" />
                <SimpleTable>
                  {acceptCmpClicked != null && <KvRow label="Button geklickt" value={acceptCmpClicked ? 'Ja' : 'Nein'} />}
                  {acceptCmpMethod && <KvRow label="Methode" value={acceptCmpMethod} />}
                  {acceptCmpDetail && <KvRow label="Selector" value={<code style={{ fontSize: '0.75rem' }}>{acceptCmpDetail}</code>} />}
                </SimpleTable>
              </>
            )}
            <CmpButtonsTable buttons={acceptCmpButtons} label="Geklickte CMP-Buttons" />
            <ScriptListTable scripts={acceptScripts} label="Screening Output (nach Accept)" />
            {acceptConsentMode && (
              <>
                <DataSectionHeader title="Consent Mode" />
                <SimpleTable>
                  <KvRow label="Modus" value={<Chip label={acceptConsentMode} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />} />
                </SimpleTable>
              </>
            )}
            {acceptDL && <DataLayerTable entries={acceptDL} label="DataLayer (nach Accept)" />}
            {ca?.detectHardcoded != null && (
              <>
                <DataSectionHeader title="Hardcoded Detection" />
                <SimpleTable>
                  <KvRow label="Hardcoded Scripts" value={ca.detectHardcoded ? 'Ja' : 'Nein'} />
                  {ca.detectHardcodedMeta && (
                    <KvRow label="Meta" value={
                      Object.entries(ca.detectHardcodedMeta)
                        .filter(([k]) => k !== 'note')
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(' · ')
                    } />
                  )}
                </SimpleTable>
              </>
            )}
            {ca?.runtimeInjectedTracking && <RuntimeInjectedTable items={ca.runtimeInjectedTracking} />}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Consent Denied */}
      <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' }, borderTop: `1px solid ${colors.silver}` }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fef2f2' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip label="Consent Denied" sx={{ bgcolor: colors.scoreRed, color: colors.white, fontWeight: 600, fontSize: '0.8rem' }} />
            <Typography variant="body2" color="text.secondary">Zustand nach Klick auf „Ablehnen"</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 2, ...accentBar(colors.scoreRed) }}>
            {(denyCmpClicked != null || denyCmpMethod) && (
              <>
                <DataSectionHeader title="CMP Interaktion" />
                <SimpleTable>
                  {denyCmpClicked != null && <KvRow label="Deny geklickt" value={denyCmpClicked ? 'Ja' : 'Nein'} />}
                  {denyCmpMethod && <KvRow label="Methode" value={denyCmpMethod} />}
                  {denyCmpDetail && <KvRow label="Detail" value={denyCmpDetail} />}
                </SimpleTable>
              </>
            )}
            <CmpButtonsTable buttons={denyCmpButtons} label="Geklickte CMP-Buttons (Deny-Flow)" />
            <ScriptListTable scripts={denyScripts} label="Screening Output (nach Deny)" />
            {denyConsentMode && (
              <>
                <DataSectionHeader title="Consent Mode" />
                <SimpleTable>
                  <KvRow label="Modus" value={<Chip label={denyConsentMode} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />} />
                </SimpleTable>
              </>
            )}
            {denyDL && <DataLayerTable entries={denyDL} label="DataLayer (nach Deny)" />}
            {cd?.detectHardcoded != null && (
              <>
                <DataSectionHeader title="Hardcoded Detection" />
                <SimpleTable>
                  <KvRow label="Hardcoded Scripts" value={cd.detectHardcoded ? 'Ja' : 'Nein'} />
                  {cd.detectHardcodedMeta && (
                    <KvRow label="Meta" value={
                      Object.entries(cd.detectHardcodedMeta)
                        .filter(([k]) => k !== 'note')
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(' · ')
                    } />
                  )}
                </SimpleTable>
              </>
            )}
            {cd?.runtimeInjectedTracking && <RuntimeInjectedTable items={cd.runtimeInjectedTracking} />}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}

/* ── Rating-Helfer ───────────────────────────────────────── */

const RATING_META: Record<Rating, { color: string; label: string; Icon: typeof CheckCircleIcon }> = {
  [Rating.G]: { color: colors.scoreGreen, label: 'Grün', Icon: CheckCircleIcon },
  [Rating.Y]: { color: colors.scoreYellow, label: 'Gelb', Icon: WarningIcon },
  [Rating.R]: { color: colors.scoreRed, label: 'Rot', Icon: CancelIcon },
};

function RatingDot({ rating, size = 22 }: { rating: Rating; size?: number }) {
  const { color, label, Icon } = RATING_META[rating];
  return (
    <Tooltip title={label}>
      <Icon sx={{ color, fontSize: size }} />
    </Tooltip>
  );
}

function CategoryProgressBar({ score, color }: { score: number; color: string }) {
  return (
    <LinearProgress
      variant="determinate"
      value={score}
      sx={{
        height: 10,
        borderRadius: 5,
        bgcolor: '#e5e7eb',
        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 5 },
      }}
    />
  );
}

function ScanRunningHint({ phase }: { phase: 'scanning' | 'evaluating' }) {
  const title = phase === 'evaluating' ? 'Daten werden ausgewertet' : 'Scan wird durchgeführt';
  const subtitle = phase === 'evaluating'
    ? 'Die Auswertung läuft — die Ergebnisse erscheinen in wenigen Sekunden.'
    : 'Die Ergebnisse erscheinen automatisch, sobald der Scan abgeschlossen ist.';
  return (
    <Paper elevation={0} sx={{ mb: 3, p: 3, border: `1px solid ${colors.silver}`, bgcolor: colors.white }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    </Paper>
  );
}

function ShareQrCard({ shareUrl, onCopy, onOpen }: { shareUrl: string; onCopy: () => void; onOpen: () => void }) {
  return (
    <Paper elevation={0} sx={{ mb: 3, p: 3, border: `1px solid ${colors.silver}`, bgcolor: colors.white }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Live-Preview per QR-Code
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Seite jetzt öffnen oder scannen. Wenn der Scan fertig ist, reicht ein Refresh für die finalen Inhalte.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ p: 1, bgcolor: colors.white, border: `1px solid ${colors.silver}`, borderRadius: 1 }}>
          <QRCodeSVG value={shareUrl} size={138} />
        </Box>
        <Box sx={{ minWidth: 260, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Share-Link
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', mb: 1 }}>
            {shareUrl}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={onCopy}
            >
              Link kopieren
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={onOpen}
            >
              Link öffnen
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

/* ── Score Overview ──────────────────────────────────────── */

function ScoreOverview({ evaluation }: { evaluation: IEvaluationResult }) {
  const ampelColor = RATING_META[evaluation.overall_rating].color;
  const ampelLabel = evaluation.overall_rating === Rating.G ? 'GRÜN'
    : evaluation.overall_rating === Rating.Y ? 'GELB' : 'ROT';

  return (
    <Paper elevation={0} sx={{ p: 4, mb: 3, bgcolor: colors.white, border: `1px solid ${colors.silver}`, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>Gesamtergebnis</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <ScoreGauge score={evaluation.overall_score} size={180} />
      </Box>
      <Chip label={`Ampel: ${ampelLabel}`} sx={{ bgcolor: ampelColor, color: '#fff', fontWeight: 600, fontSize: '0.95rem', px: 2, py: 0.5 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {evaluation.check_results.filter(c => !c.skipped).length} Checks ausgewertet
        &nbsp;·&nbsp;
        {evaluation.check_results.filter(c => c.skipped).length} übersprungen
      </Typography>
    </Paper>
  );
}

/* ── Kategorie-Karten ────────────────────────────────────── */

function CategoryCard({ cat }: { cat: ICategoryEval }) {
  const ampelColor = RATING_META[cat.rating].color;
  return (
    <Paper elevation={0} sx={{ p: 3, flex: '1 1 280px', maxWidth: 380, bgcolor: colors.white, border: `1px solid ${colors.silver}`, borderTop: `4px solid ${cat.color ?? ampelColor}` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{cat.display_name}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 600, color: ampelColor }}>{cat.score}</Typography>
      </Box>
      <CategoryProgressBar score={cat.score} color={ampelColor} />
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        <Chip size="small" label={`${cat.checks_green} G`} sx={{ bgcolor: colors.scoreGreen, color: '#fff', fontWeight: 600 }} />
        <Chip size="small" label={`${cat.checks_yellow} Y`} sx={{ bgcolor: colors.scoreYellow, color: '#fff', fontWeight: 600 }} />
        <Chip size="small" label={`${cat.checks_red} R`} sx={{ bgcolor: colors.scoreRed, color: '#fff', fontWeight: 600 }} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>{cat.checks_total} Checks</Typography>
      </Box>
      {/* Scoring Adjustments (Konfigurator+) */}
      {cat.adjustments && cat.adjustments.length > 0 && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${colors.silver}` }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: colors.black60, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Scoring Adjustments
          </Typography>
          {cat.base_score != null && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
              Basis-Score: {cat.base_score} → Final: {cat.score}
            </Typography>
          )}
          {cat.adjustments.map((adj, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Chip
                size="small"
                label={`${adj.direction}${adj.impact_percent}%`}
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  height: 20,
                  bgcolor: adj.direction === '+' ? '#dcfce7' : '#fee2e2',
                  color: adj.direction === '+' ? '#166534' : '#991b1b',
                }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{adj.rule_name}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

/* ── Einzelcheck-Zeile ───────────────────────────────────── */

function CheckResultRow({ check }: { check: ICheckEval }) {
  // Title source of truth: ScoringRule.name when this row represents a scoring rule
  // (config_plus, one row per rule); otherwise the check's own title (legacy / unbound).
  // Defensive fallback to check_code keeps the row identifiable even if title is empty.
  const displayTitle = check.title || check.check_code;

  if (check.skipped) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2, px: 2, opacity: 0.55 }}>
        <RemoveCircleOutlineIcon sx={{ color: colors.silver, fontSize: 22 }} />
        <Typography variant="body2" sx={{ flex: 1 }}>{displayTitle}</Typography>
        <Chip size="small" label="übersprungen" variant="outlined" sx={{ fontSize: '0.7rem' }} />
      </Box>
    );
  }

  const rv = check.raw_value as Record<string, unknown> | null;
  const hasFieldValue = rv != null && 'field_value' in rv;
  const hasLegacyData = rv != null && !hasFieldValue && Object.keys(rv).some((k) => k !== 'groupResults');

  return (
    <Accordion elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <RatingDot rating={check.rating} />
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{displayTitle}</Typography>
          {check.confidence === 'low' && (
            <Chip size="small" label="unsicher" color="warning" variant="outlined" sx={{ fontSize: '0.65rem', mr: 0.5 }} />
          )}
          <Chip size="small" label={check.weight} variant="outlined" sx={{ fontSize: '0.7rem', minWidth: 56, textAlign: 'center' }} />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        {check.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{check.description}</Typography>}
        {check.info_box && (
          <Box sx={{ mb: 1, p: 1.5, bgcolor: colors.lightGrey, borderRadius: 1, border: `1px solid ${colors.silver}` }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{check.info_box}</Typography>
          </Box>
        )}
        {check.matched_rule && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Ergebnis:</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{check.matched_rule}</Typography>
          </Box>
        )}
        {hasFieldValue && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Gefundener Wert
            </Typography>
            {!!rv!.field_path && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace', mt: 0.25 }}>
                Pfad: {String(rv!.field_path)}
              </Typography>
            )}
            <Box sx={{ mt: 0.5, p: 1, borderRadius: 1, bgcolor: '#fff', border: `1px solid ${colors.silver}`, maxHeight: 160, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.78rem' }}>
                {rv!.field_value === null || rv!.field_value === undefined
                  ? '(kein Wert gefunden)'
                  : typeof rv!.field_value === 'string'
                    ? rv!.field_value as string
                    : JSON.stringify(rv!.field_value, null, 2)}
              </Typography>
            </Box>
          </Box>
        )}
        {hasLegacyData && (() => {
          const { groupResults: _gr, ...rest } = rv as Record<string, unknown>;
          return (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: colors.lightGrey, borderRadius: 1, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}>
              <pre style={{ margin: 0 }}>{JSON.stringify(rest, null, 2)}</pre>
            </Box>
          );
        })()}
        {/* Nur Erklaertexte aus einem Scoring-Fall. Check-Erklaerungen (und die in alten
            Scorecards eingebetonierten) werden hier bewusst nicht mehr angezeigt. */}
        {check.explanation_source === 'scoring_branch' && check.explanation && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#0369a1', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
              Erklärung
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#0c4a6e' }}>{check.explanation}</Typography>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

/* ── Check-Liste gruppiert nach Kategorie ────────────────── */

function CheckResultList({ evaluation }: { evaluation: IEvaluationResult }) {
  const grouped = useMemo(() => {
    const map = new Map<string, { catName: string; checks: ICheckEval[] }>();
    for (const cat of evaluation.categories) {
      map.set(cat.category_id, { catName: cat.display_name, checks: [] });
    }
    for (const cr of evaluation.check_results) {
      const entry = map.get(cr.category_id);
      if (entry) entry.checks.push(cr);
    }
    return Array.from(map.values());
  }, [evaluation]);

  return (
    <Paper elevation={0} sx={{ mb: 3, border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
      <Typography variant="h6" sx={{ p: 3, pb: 1, fontWeight: 600 }}>Einzelergebnisse</Typography>
      {grouped.map((group) => (
        <Box key={group.catName}>
          <Divider />
          <Typography variant="subtitle2" sx={{ px: 2, py: 1.2, bgcolor: colors.lightGrey, fontWeight: 600, letterSpacing: 0.5 }}>
            {group.catName}
          </Typography>
          {group.checks.length > 0 ? (
            group.checks.map((cr, idx) => (
              <CheckResultRow
                // One CheckResult row per scoring rule (config_plus) or per check (legacy).
                // `scoring_rule_id` is set in config_plus mode; absent for legacy rows. The
                // title comes ready-to-display from the backend — no branch suffix handling
                // is needed here anymore.
                key={`${cr.check_code}|${cr.scoring_rule_id ?? ''}|${idx}`}
                check={cr}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5, fontStyle: 'italic' }}>
              Keine Checks in dieser Kategorie ausgewertet
            </Typography>
          )}
        </Box>
      ))}
    </Paper>
  );
}

/* ── Handlungsempfehlungen + Quick Wins (Konfigurator Plus) ── */

const MAX_QUICK_WINS = 3;

/**
 * Alle Handlungsempfehlungen des Scans (Erklaertexte der gefeuerten Faelle). Bis zu drei
 * aktive stehen als Quick Wins auf der Scorecard. Ohne eigene Auswahl waehlt das System
 * aus den als Quick Win markierten Faellen, je Kategorie zuerst einen. Umschalten duerfen
 * Admin und DigA; die Schalter tauchen im PDF nicht auf (pdf-hide), ein Chip markiert dort
 * die aktiven.
 */
function RecommendationPanel({ scanId, recommendations, selection, canEdit }: {
  scanId: number;
  recommendations: IScanRecommendation[];
  selection: QuickWinSelectionState | null | undefined;
  canEdit: boolean;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeKeys = recommendations.filter((rec) => rec.active).map((rec) => rec.key);
  const editable = canEdit && selection?.selectable === true;

  const save = async (keys: string[] | null) => {
    setSaving(true);
    setError(null);
    const res = await api.put<{
      recommendations: IScanRecommendation[];
      quick_wins: IQuickWin[];
      quick_win_selection: QuickWinSelectionState;
    }>(`/scans/${scanId}/quick-wins`, { keys });
    if (res.success) dispatch(setScanQuickWins(res.data));
    else setError(res.error.message);
    setSaving(false);
  };

  const switchHint = (rec: IScanRecommendation, limitReached: boolean): string => {
    if (!canEdit) return 'Quick Wins wählen Admin und DigA';
    if (!editable) return 'Noch nicht speicherbar (Migration fehlt)';
    if (limitReached) return `Höchstens ${MAX_QUICK_WINS} Quick Wins — erst einen abwählen`;
    return rec.active ? 'Von der Scorecard nehmen' : 'Als Quick Win auf die Scorecard';
  };

  return (
    <Paper elevation={0} sx={{ mb: 3, border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
      <Box sx={{ p: 3, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Handlungsempfehlungen</Typography>
          <Typography variant="body2" color="text.secondary">
            Aktive Empfehlungen stehen als Quick Wins auf der Scorecard, höchstens {MAX_QUICK_WINS}.{' '}
            {selection?.manual
              ? 'Eigene Auswahl.'
              : 'Systemauswahl aus den als Quick Win markierten Fällen, je Kategorie zuerst einer.'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={`${activeKeys.length} / ${MAX_QUICK_WINS} Quick Wins`}
            sx={{ fontWeight: 600, bgcolor: colors.black, color: colors.lime }}
          />
          {editable && selection?.manual && (
            <Button className="pdf-hide" size="small" onClick={() => void save(null)} disabled={saving}>
              Systemauswahl
            </Button>
          )}
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mx: 3, mb: 1.5 }}>{error}</Alert>}
      {canEdit && selection && !selection.selectable && (
        <Alert className="pdf-hide" severity="info" sx={{ mx: 3, mb: 1.5 }}>
          Die Auswahl lässt sich in dieser Umgebung noch nicht speichern — Migration 20261103_02 fehlt.
        </Alert>
      )}
      {recommendations.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 2.5, fontStyle: 'italic' }}>
          Keine Handlungsempfehlungen: Für die gefeuerten Fälle ist kein Erklärtext aktiv — oder der Scan
          wurde vor 11/2026 ausgewertet und braucht eine Neuauswertung.
        </Typography>
      ) : (
        recommendations.map((rec, idx) => {
          const limitReached = !rec.active && activeKeys.length >= MAX_QUICK_WINS;
          return (
            <Box key={rec.key}>
              {idx > 0 && <Divider />}
              <Box sx={{ px: 3, py: 1.75, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Tooltip title={switchHint(rec, limitReached)}>
                  <span className="pdf-hide">
                    <Switch
                      checked={rec.active}
                      disabled={!editable || saving || limitReached}
                      onChange={(e) => void save(e.target.checked ? [...activeKeys, rec.key] : activeKeys.filter((k) => k !== rec.key))}
                      slotProps={{ input: { 'aria-label': `Quick Win: ${rec.title}` } }}
                    />
                  </span>
                </Tooltip>
                <Box sx={{ flex: 1, minWidth: 0, pt: 0.75 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                    <RatingDot rating={rec.rating} size={18} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{rec.title}</Typography>
                    <Chip size="small" variant="outlined" label={rec.category_name} sx={{ fontSize: '0.7rem', height: 20 }} />
                    {rec.active && (
                      <Chip size="small" label="Quick Win" sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600, bgcolor: colors.lime, color: colors.black }} />
                    )}
                    {rec.quick_win && !rec.active && (
                      <Chip size="small" label="als Quick Win markiert" sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#f3f4f6', color: '#374151' }} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>{rec.text}</Typography>
                </Box>
              </Box>
            </Box>
          );
        })
      )}
    </Paper>
  );
}

/* ── Markdown-Export ─────────────────────────────────────── */

function ratingLabelMd(r: Rating): string {
  return r === Rating.G ? '🟢 Grün' : r === Rating.Y ? '🟡 Gelb' : '🔴 Rot';
}

interface MarkdownReportArgs {
  scan: { url: string; created_at: string; scan_template?: { display_name?: string } | null };
  evaluation: IEvaluationResult;
  quickWins: { title: string; description: string | null }[];
  disclaimer: string | null;
  scoreCardLabel: string;
}

function buildMarkdownReport({ scan, evaluation, quickWins, disclaimer, scoreCardLabel }: MarkdownReportArgs): string {
  const lines: string[] = [];

  lines.push(`# Domain Screener — ${scan.url}`, '');
  const dateStr = new Date(scan.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  lines.push(`**Scan vom:** ${dateStr}  `);
  lines.push(`**Scorecard:** ${scoreCardLabel}  `);
  if (scan.scan_template?.display_name) lines.push(`**Scan-Template:** ${scan.scan_template.display_name}  `);
  lines.push('');

  lines.push('## Gesamtergebnis', '');
  lines.push(`**Score: ${Math.round(evaluation.overall_score)} / 100 — ${ratingLabelMd(evaluation.overall_rating)}**`, '');

  if (evaluation.categories.length > 0) {
    lines.push('## Kategorien', '');
    for (const cat of evaluation.categories) {
      lines.push(`### ${cat.display_name} — ${Math.round(cat.score)} / 100 (${ratingLabelMd(cat.rating)})`, '');
      lines.push(`- Checks: ${cat.checks_green} grün, ${cat.checks_yellow} gelb, ${cat.checks_red} rot`);
      if (cat.adjustments && cat.adjustments.length > 0) {
        lines.push('- Anpassungen:');
        for (const adj of cat.adjustments) {
          lines.push(`  - ${adj.direction}${adj.impact_percent}% — ${adj.rule_name}`);
        }
      }
      lines.push('');
    }
  }

  lines.push('## Einzelergebnisse', '');
  for (const cat of evaluation.categories) {
    const checks = evaluation.check_results.filter((cr) => cr.category_id === cat.category_id);
    lines.push(`### ${cat.display_name}`, '');
    if (checks.length === 0) {
      lines.push('_Keine Checks in dieser Kategorie ausgewertet._', '');
      continue;
    }
    for (const cr of checks) {
      const title = cr.title || cr.check_code;
      if (cr.skipped) {
        lines.push(`#### ⚪ ${title} (übersprungen)`, '');
        continue;
      }
      lines.push(`#### ${ratingLabelMd(cr.rating)} ${title}`, '');
      lines.push(`- **Gewichtung:** ${cr.weight}`);
      if (cr.matched_rule) lines.push(`- **Ergebnis:** ${cr.matched_rule}`);
      if (cr.description) lines.push(`- ${cr.description}`);
      // wie in der Anzeige: nur Fall-Erklaerungen, keine Check-Texte
      if (cr.explanation_source === 'scoring_branch' && cr.explanation) {
        lines.push(`- **Erklärung:** ${cr.explanation}`);
      }
      lines.push('');
    }
  }

  if (quickWins.length > 0) {
    lines.push('## Quick Wins & Hinweise', '');
    for (const qw of quickWins) {
      lines.push(`- **${qw.title}**${qw.description ? ` — ${qw.description}` : ''}`);
    }
    lines.push('');
  }

  if (disclaimer) {
    lines.push('---', '', `_${disclaimer}_`, '');
  }

  return lines.join('\n');
}

/* ── Haupt-Seite ─────────────────────────────────────────── */

export default function ScanStatusPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const userRole = useSelector((state: RootState) => state.auth.user?.role);
  const canEditQuickWins = userRole === UserRole.ADMIN || userRole === UserRole.DIGA;
  const navigate = useNavigate();
  const { currentScan, loading, error } = useSelector((state: RootState) => state.scan);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stabilizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevLabelRef = useRef<string | undefined>(undefined);
  const printRef = useRef<HTMLDivElement>(null);
  const [shareSnackbar, setShareSnackbar] = useState(false);
  const [stabilizing, setStabilizing] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);

  const scanId = parseInt(id ?? '', 10);

  const poll = useCallback(() => {
    if (!isNaN(scanId)) dispatch(fetchScan(scanId));
  }, [dispatch, scanId]);

  // Stale Scan-Daten aus dem Store entfernen, wenn zu einem anderen Scan navigiert wird.
  // Auch prevLabelRef und stabilizing zurücksetzen, damit der Stabilisierungs-Timer
  // bei archivierten Scans nicht fälschlich anspringt oder hängen bleibt.
  useEffect(() => {
    dispatch(clearCurrentScan());
    setStabilizing(false);
    prevLabelRef.current = undefined;
  }, [scanId, dispatch]);

  useEffect(() => { poll(); }, [poll]);

  useEffect(() => {
    const labelKey = currentScan?.scan?.scan_label?.key_name as
      | (typeof TERMINAL_SCAN_LABEL_KEYS)[number]
      | string
      | undefined;
    const isTerminal = labelKey != null && (TERMINAL_SCAN_LABEL_KEYS as readonly string[]).includes(labelKey);
    if (labelKey && !isTerminal) {
      dispatch(setPolling(true));
      intervalRef.current = setInterval(poll, 5000);
    } else {
      dispatch(setPolling(false));
      if (intervalRef.current) clearInterval(intervalRef.current);

      if (labelKey === SCAN_LABEL_KEYS.COMPLETED && !currentScan?.evaluation && !stabilizing) {
        intervalRef.current = setInterval(poll, 1500);
      }
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [currentScan?.scan?.scan_label?.key_name, currentScan?.evaluation, stabilizing, poll, dispatch]);

  /**
   * Stabilisierungs-Effekt: Nach COMPLETED 10 Sekunden warten, damit scores_json
   * sicher final in der DB steht. Danach sofort anzeigen.
   * Nur bei Live-Übergängen aktiv (prevLabel war nicht-terminal), nicht bei
   * direkter Navigation zu einem bereits fertigen Scan.
   */
  useEffect(() => {
    const labelKey = currentScan?.scan?.scan_label?.key_name;
    const prevLabel = prevLabelRef.current;
    prevLabelRef.current = labelKey;

    const wasNonTerminal =
      prevLabel != null && !(TERMINAL_SCAN_LABEL_KEYS as readonly string[]).includes(prevLabel);
    const justCompleted = labelKey === SCAN_LABEL_KEYS.COMPLETED && wasNonTerminal;

    if (!justCompleted) return;

    setStabilizing(true);
    if (stabilizeTimerRef.current) clearTimeout(stabilizeTimerRef.current);

    stabilizeTimerRef.current = setTimeout(() => {
      poll();
      setStabilizing(false);
    }, 10_000);

    return () => {
      if (stabilizeTimerRef.current) clearTimeout(stabilizeTimerRef.current);
    };
    // poll ist stabil (useCallback), wird bewusst nicht als Dep. gelistet
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScan?.scan?.scan_label?.key_name]);

  const handleShare = () => {
    const token = (currentScan?.scan?.score_card as { share_token?: string } | undefined)?.share_token;
    if (token) {
      const url = buildShareUrl(token, selectedScoreCardTemplateId);
      navigator.clipboard.writeText(url);
      setShareSnackbar(true);
    }
  };

  const handleOpenShare = () => {
    const token = (currentScan?.scan?.score_card as { share_token?: string } | undefined)?.share_token;
    if (token) {
      const url = buildShareUrl(token, selectedScoreCardTemplateId);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadMenuAnchor(null);
    if (!printRef.current) return;
    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');

    const container = printRef.current;

    const pdfHeader = container.querySelector('.pdf-only-header') as HTMLElement | null;
    if (pdfHeader) pdfHeader.style.display = 'block';

    const collapsedPanels = container.querySelectorAll<HTMLElement>('.MuiAccordionDetails-root');
    const originalStyles: string[] = [];
    collapsedPanels.forEach((panel) => {
      originalStyles.push(panel.style.display);
      panel.style.display = 'block';
      panel.style.height = 'auto';
      panel.style.overflow = 'visible';
      panel.style.visibility = 'visible';
    });

    const collapsedWrappers = container.querySelectorAll<HTMLElement>('.MuiCollapse-root');
    const wrapperStyles: { display: string; height: string; overflow: string; visibility: string }[] = [];
    collapsedWrappers.forEach((wrapper) => {
      wrapperStyles.push({
        display: wrapper.style.display,
        height: wrapper.style.height,
        overflow: wrapper.style.overflow,
        visibility: wrapper.style.visibility,
      });
      wrapper.style.display = 'block';
      wrapper.style.height = 'auto';
      wrapper.style.overflow = 'visible';
      wrapper.style.visibility = 'visible';
    });

    await new Promise((r) => setTimeout(r, 200));

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      // Bedienelemente (Quick-Win-Schalter) gehoeren nicht ins PDF
      ignoreElements: (el) => el.classList.contains('pdf-hide'),
      windowWidth: container.scrollWidth,
      windowHeight: container.scrollHeight,
    });

    if (pdfHeader) pdfHeader.style.display = 'none';
    collapsedPanels.forEach((panel, i) => {
      panel.style.display = originalStyles[i];
      panel.style.height = '';
      panel.style.overflow = '';
      panel.style.visibility = '';
    });
    collapsedWrappers.forEach((wrapper, i) => {
      wrapper.style.display = wrapperStyles[i].display;
      wrapper.style.height = wrapperStyles[i].height;
      wrapper.style.overflow = wrapperStyles[i].overflow;
      wrapper.style.visibility = wrapperStyles[i].visibility;
    });

    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pdf = new jsPDF('p', 'mm', 'a4');

    if (imgHeight <= pageHeight) {
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight);
    } else {
      let y = 0;
      while (y < imgHeight) {
        if (y > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, -y, imgWidth, imgHeight);
        y += pageHeight;
      }
    }

    const scanUrl = currentScan?.scan?.url ?? 'scan';
    const domain = scanUrl.replace(/https?:\/\//, '').replace(/[/\\?#]/g, '_').slice(0, 40);
    pdf.save(`scan-ergebnis_${domain}.pdf`);
  };

  const handleDownloadMarkdown = () => {
    setDownloadMenuAnchor(null);
    const mdScan = currentScan?.scan;
    const mdEval = currentScan?.evaluation;
    if (!mdScan || !mdEval) return;

    const templateId = normalizeScoreCardHtmlTemplate(
      searchParams.get('template') ?? mdScan.scan_template?.score_card_html_template,
    );
    const md = buildMarkdownReport({
      scan: mdScan,
      evaluation: mdEval,
      quickWins: (currentScan?.quick_wins ?? []) as { title: string; description: string | null }[],
      disclaimer: (currentScan?.disclaimer as string | null) ?? null,
      scoreCardLabel: SCORE_CARD_HTML_TEMPLATE_LABELS[templateId],
    });

    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const domain = mdScan.url.replace(/https?:\/\//, '').replace(/[/\\?#]/g, '_').slice(0, 40);
    a.href = url;
    a.download = `scan-ergebnis_${domain}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const screening = useMemo(
    () => extractScreeningData(currentScan?.scan?.score_card?.playwright_raw ?? null),
    [currentScan?.scan?.score_card?.playwright_raw],
  );

  if (loading && !currentScan) return <LoadingSpinner message="Scan wird geladen..." />;
  if (!currentScan) return <Alert severity="error" sx={{ m: 2 }}>{error ?? 'Scan nicht gefunden'}</Alert>;

  const { scan, evaluation, quick_wins, disclaimer, recommendations, quick_win_selection } = currentScan;
  const selectedScoreCardTemplateId = normalizeScoreCardHtmlTemplate(
    searchParams.get('template') ?? scan.scan_template?.score_card_html_template,
  );
  const selectedScoreCardTemplateLabel = SCORE_CARD_HTML_TEMPLATE_LABELS[selectedScoreCardTemplateId];
  const labelKey = scan.scan_label?.key_name;
  const isCompleted = labelKey === SCAN_LABEL_KEYS.COMPLETED;
  const isFailed =
    labelKey === SCAN_LABEL_KEYS.FAILED ||
    labelKey === SCAN_LABEL_KEYS.TASK_CREATION_FAILED ||
    labelKey === SCAN_LABEL_KEYS.WORKFLOW_UNREACHABLE;
  const isPending = !isCompleted && !isFailed;
  const shareToken = scan.score_card?.share_token;
  const shareUrl = shareToken ? buildShareUrl(shareToken, selectedScoreCardTemplateId) : null;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/scan')} sx={{ mb: 2 }}>
        Neuer Scan
      </Button>

      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: colors.lightGrey, border: `1px solid ${colors.silver}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Domain Screener — {scan.url}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              Scan vom {new Date(scan.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              &nbsp;·&nbsp;Scorecard: {selectedScoreCardTemplateLabel}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              <ScanStatusChip scanLabel={scan.scan_label} />
              {scan.scan_template?.display_name && (
                <Chip
                  size="small"
                  label={`📄 ${scan.scan_template.display_name}`}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: colors.black,
                    color: colors.lime,
                  }}
                />
              )}
              {scan.scan_template?.source && (
                <Chip
                  size="small"
                  label={scan.scan_template.source === 'config_plus' ? '⚙️ Konfiguration+' : '📋 Legacy'}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: scan.scan_template.source === 'config_plus' ? '#7c3aed' : colors.black60,
                    color: colors.white,
                  }}
                />
              )}
            </Box>
          </Box>
          {isCompleted && !stabilizing && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Herunterladen">
                <IconButton onClick={(e) => setDownloadMenuAnchor(e.currentTarget)} sx={{ bgcolor: colors.black, color: colors.white, '&:hover': { bgcolor: colors.black85 } }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={downloadMenuAnchor}
                open={Boolean(downloadMenuAnchor)}
                onClose={() => setDownloadMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleDownloadPdf}>
                  <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Als PDF herunterladen</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDownloadMarkdown}>
                  <ListItemIcon><ArticleIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Als Markdown herunterladen</ListItemText>
                </MenuItem>
              </Menu>
              <Tooltip title="Share-Link kopieren">
                <IconButton onClick={handleShare} sx={{ bgcolor: colors.lime, '&:hover': { bgcolor: '#b8e600' } }}>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Share-Link öffnen">
                <IconButton
                  onClick={handleOpenShare}
                  sx={{ bgcolor: colors.lime, '&:hover': { bgcolor: '#b8e600' } }}
                >
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Paper>

      {isPending && <ScanRunningHint phase="scanning" />}
      {isCompleted && stabilizing && <ScanRunningHint phase="evaluating" />}
      {shareUrl && <ShareQrCard shareUrl={shareUrl} onCopy={handleShare} onOpen={handleOpenShare} />}
      {isFailed && <Alert severity="error" sx={{ mb: 3 }}>Der Scan ist fehlgeschlagen. Bitte versuche es erneut.</Alert>}

      {isCompleted && !stabilizing && !evaluation && (
        <LoadingSpinner message="Auswertung wird geladen..." />
      )}

      {isCompleted && !stabilizing && evaluation && (
        <Box ref={printRef} sx={{ bgcolor: colors.white }}>
          {/* PDF-Header (nur im PDF sichtbar, im UI versteckt) */}
          <Box className="pdf-only-header" sx={{ display: 'none', p: 3, mb: 2, bgcolor: colors.lightGrey, border: `1px solid ${colors.silver}` }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Domain Screener — {scan.url}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Scan vom {new Date(scan.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </Typography>
          </Box>
          {/* Screening-Vergleichstabelle */}
          {screening && <ScreeningTable screening={screening} />}

          {/* Rohdaten-Explorer (3 Dropdowns) */}
          {scan.score_card?.playwright_raw && (
            <RawDataExplorer raw={scan.score_card.playwright_raw} />
          )}

          {/* Evaluation-Ergebnisse */}
          {evaluation && (
            <>
              <ScoreOverview evaluation={evaluation} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                {evaluation.categories.map((cat) => (
                  <CategoryCard key={cat.category_id} cat={cat} />
                ))}
              </Box>
              <CheckResultList evaluation={evaluation} />
              {/* Konfigurator Plus: Handlungsempfehlungen, die aktiven sind die Quick Wins der Scorecard */}
              {recommendations && (
                <RecommendationPanel
                  scanId={scan.id}
                  recommendations={recommendations}
                  selection={quick_win_selection}
                  canEdit={canEditQuickWins}
                />
              )}
            </>
          )}

          {/* Quick Wins & Hinweise (Legacy-Auswertungen aus dem alten Konfigurator) */}
          {!recommendations && quick_wins && quick_wins.length > 0 && (
            <Paper elevation={0} sx={{ mb: 3, border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
              <Typography variant="h6" sx={{ p: 3, pb: 1, fontWeight: 600 }}>Quick Wins &amp; Hinweise</Typography>
              {quick_wins.map((qw, idx) => (
                <Box key={qw.id}>
                  {idx > 0 && <Divider />}
                  <Box sx={{ px: 3, py: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{qw.title}</Typography>
                    {qw.description && <Typography variant="body2" color="text.secondary">{qw.description}</Typography>}
                  </Box>
                </Box>
              ))}
            </Paper>
          )}

          {/* Disclaimer */}
          {disclaimer && (
            <Paper elevation={0} sx={{ p: 3, bgcolor: colors.lightGrey, border: `1px solid ${colors.silver}` }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{disclaimer}</Typography>
            </Paper>
          )}
        </Box>
      )}

      <Snackbar open={shareSnackbar} autoHideDuration={3000} onClose={() => setShareSnackbar(false)} message="Share-Link in die Zwischenablage kopiert!" />
    </Box>
  );
}
